import type { Palette } from '@sd/contracts';
import { createContext, useContext, useEffect, useState, type CSSProperties } from 'react';

export interface AvatarPerson {
  /** With `avatar`, the photo is shown instead of the initials once it has loaded. */
  id?: string;
  shortName: string;
  palette: Palette;
  status?: 'active' | 'deactivated';
  online?: boolean;
  avatar?: { version: string } | undefined;
}

/** Fetches photos with the signed-in session (the messenger client). Without one, avatars show initials. */
export interface AvatarSource {
  avatarUrl(userId: string, version: string): Promise<string>;
  releaseAvatar(userId: string, version: string): void;
}

export const AvatarSourceContext = createContext<AvatarSource | null>(null);

/** The object URL of the person's photo once it loaded; until then, and if it cannot be loaded, the initials show. */
function usePhoto(person: AvatarPerson | null | undefined): string | null {
  const source = useContext(AvatarSourceContext);
  const id = person?.id;
  // Deactivated people show the neutral placeholder.
  const version = person?.status === 'deactivated' ? undefined : person?.avatar?.version;
  const key = source && id && version ? `${id}/${version}` : null;
  const [shown, setShown] = useState<{ key: string; url: string } | null>(null);
  useEffect(() => {
    if (!source || !id || !version) return undefined;
    let live = true;
    source.avatarUrl(id, version).then(
      (url) => {
        if (live) setShown({ key: `${id}/${version}`, url });
      },
      () => {},
    );
    return () => {
      live = false;
      source.releaseAvatar(id, version);
    };
  }, [source, id, version]);
  return shown && shown.key === key ? shown.url : null;
}

export interface AvatarProps {
  person: AvatarPerson | null | undefined;
  /** Diameter in px (sets the design's `--s`). Defaults to the CSS context size. */
  size?: number;
  /** `mini` inside an avatar group; `nodot` hides presence; `xs` shows one character. */
  mini?: boolean;
  noDot?: boolean;
  xs?: boolean;
}

/** `.av` gradient avatar with the two-letter label, or the person's photo over it (decorative; name is rendered elsewhere). */
export function Avatar({ person, size, mini, noDot, xs }: AvatarProps) {
  const p = person ?? { shortName: '?', palette: 'p7' as const };
  const photo = usePhoto(person);
  const [broken, setBroken] = useState<string | null>(null);
  const off = p.status === 'deactivated';
  const shown = photo && photo !== broken ? photo : null;
  const cls = ['av', p.palette, off && 'off', mini && 'mini', shown && 'photo'].filter(Boolean).join(' ');
  const style = size ? ({ '--s': `${size}px` } as CSSProperties) : undefined;
  const label = xs ? [...p.shortName][0] ?? '?' : p.shortName;
  return (
    <span className={cls} style={style} aria-hidden="true">
      {label}
      {shown ? <img className="av-img" src={shown} alt="" draggable={false} onError={() => setBroken(shown)} /> : null}
      {p.online && !noDot && !off ? <span className="on-dot" /> : null}
    </span>
  );
}

/** `.avg` two-avatar group used for group conversations. */
export function AvatarGroup({ people, size, off }: { people: readonly (AvatarPerson | null | undefined)[]; size?: number; off?: boolean }) {
  const style = size ? ({ '--s': `${size}px` } as CSSProperties) : undefined;
  return (
    <span className={`avg${off ? ' off' : ''}`} style={style} aria-hidden="true">
      <Avatar person={people[0]} mini noDot />
      <Avatar person={people[1] ?? people[0]} mini noDot />
    </span>
  );
}

/** `.stack` of up to 4 small avatars with "+N". */
export function AvatarStack({ people, total }: { people: readonly AvatarPerson[]; total: number }) {
  const shown = people.slice(0, 4);
  return (
    <span className="stack" aria-hidden="true">
      {shown.map((person, index) => (
        <Avatar key={index} person={person} noDot xs />
      ))}
      {total > shown.length ? <span className="more">+{total - shown.length}</span> : null}
    </span>
  );
}

const SERVER_SLOTS = ['s1', 's2', 's3', 's4', 's5', 's6'] as const;

/** Stable gradient slot for a company server (by instance id). */
export function serverSlotOf(serverId: string): (typeof SERVER_SLOTS)[number] {
  let hash = 0;
  for (const char of serverId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return SERVER_SLOTS[hash % SERVER_SLOTS.length]!;
}

/** Two-character company label: "누리디자인랩" → "누리", "SHARE.DEV" → "SD", "Acme Corp" → "AC". */
export function companyShortName(companyName: string): string {
  const name = companyName.trim();
  if (!name) return '?';
  if (/^[가-힣]/.test(name)) return [...name].slice(0, 2).join('');
  const words = name.split(/[\s._\-/·&]+/u).filter(Boolean);
  if (words.length >= 2) return `${[...words[0]!][0]}${[...words[1]!][0]}`.toUpperCase();
  return [...(words[0] ?? name)].slice(0, 2).join('').toUpperCase();
}

export interface ServerBadgeProps {
  serverId: string;
  companyName: string;
  className?: string;
  style?: CSSProperties;
}

/** `.srv` company badge (rail, chips, connect screen). */
export function ServerBadge({ serverId, companyName, className, style }: ServerBadgeProps) {
  return (
    <span className={['srv', serverSlotOf(serverId), className].filter(Boolean).join(' ')} style={style} aria-hidden="true">
      {companyShortName(companyName)}
    </span>
  );
}
